import { authService } from './authService';

const API_BASE_URL = import.meta.env.VITE_API_BASE_URL || '';

/**
 * Unwraps the IActionResult serialization wrapper returned by Azure Functions
 * isolated worker (ConfigureFunctionsWorkerDefaults). Responses come back as:
 *   PascalCase: { "Value": <payload>, "Formatters": [], "ContentTypes": [], "StatusCode": 200 }
 *   camelCase:  { "value": <payload>, "formatters": [], "contentTypes": [], "statusCode": 200 }
 * This extracts the payload property when the wrapper is detected in either casing.
 *
 * IMPORTANT: Azure Functions isolated worker always returns HTTP 200 even when the
 * IActionResult is a NotFoundObjectResult (404), BadRequestObjectResult (400), etc.
 * The real status code is inside the wrapper. We must check it and throw if it
 * indicates an error, otherwise callers will think the request succeeded.
 */
export function unwrapResponse<T>(data: unknown): T {
  if (data != null && typeof data === 'object') {
    const obj = data as Record<string, unknown>;

    // PascalCase wrapper (legacy / before camelCase serializer config)
    if ('Value' in obj && 'StatusCode' in obj && 'Formatters' in obj) {
      const innerStatus = obj.StatusCode as number;
      if (innerStatus >= 400) {
        const payload = obj.Value as Record<string, unknown> | undefined;
        const message = (payload && typeof payload === 'object' ? (payload.error || payload.message) : undefined) as string
          || `Request failed (status ${innerStatus})`;
        throw new ApiError(innerStatus, innerStatus === 404 ? 'NOT_FOUND' : 'REQUEST_FAILED', String(message), payload);
      }
      return obj.Value as T;
    }

    // camelCase wrapper (after camelCase serializer config)
    if ('value' in obj && 'statusCode' in obj && 'formatters' in obj) {
      const innerStatus = obj.statusCode as number;
      if (innerStatus >= 400) {
        const payload = obj.value as Record<string, unknown> | undefined;
        const message = (payload && typeof payload === 'object' ? (payload.error || payload.message) : undefined) as string
          || `Request failed (status ${innerStatus})`;
        throw new ApiError(innerStatus, innerStatus === 404 ? 'NOT_FOUND' : 'REQUEST_FAILED', String(message), payload);
      }
      return obj.value as T;
    }
  }
  return data as T;
}

export interface ApiRequestOptions {
  headers?: Record<string, string>;
  params?: Record<string, string | number | boolean>;
  tenantId?: string;
  body?: string;
}

export class ApiError extends Error {
  constructor(
    public status: number,
    public code: string,
    message: string,
    public details?: Record<string, unknown>
  ) {
    super(message);
    this.name = 'ApiError';
  }

  static fromResponse(response: Response, data?: Record<string, unknown>): ApiError {
    switch (response.status) {
      case 400:
        return new ApiError(400, 'BAD_REQUEST',
          typeof data?.error === 'string' && data.error ? data.error
            : typeof data?.message === 'string' && data.message ? data.message : 'Invalid request', data);
      case 401:
        return new ApiError(401, 'UNAUTHORIZED', 'Authentication required');
      case 403:
        return new ApiError(403, 'FORBIDDEN', 'Access denied');
      case 404:
        return new ApiError(404, 'NOT_FOUND',
          typeof data?.error === 'string' && data.error ? data.error
            : typeof data?.message === 'string' && data.message ? data.message : 'Resource not found',
          data && Object.keys(data).length ? data : undefined);
      case 409:
        return new ApiError(409, 'REQUEST_FAILED',
          typeof data?.error === 'string' && data.error ? data.error
            : typeof data?.message === 'string' && data.message ? data.message : 'An error occurred', data);
      case 429:
        return new ApiError(429, 'RATE_LIMITED', 'Too many requests');
      case 500:
        return new ApiError(500, 'INTERNAL_ERROR', 'Server error');
      case 503:
        if (data?.errorCode === 'EXCHANGE_CREDENTIAL_CONFIGURATION_INVALID' &&
            typeof data.error === 'string' && data.error.length > 0) {
          return new ApiError(503, 'EXCHANGE_CREDENTIAL_CONFIGURATION_INVALID', data.error);
        }
        return new ApiError(503, 'UNKNOWN', 'An error occurred');
      default:
        return new ApiError(response.status, 'UNKNOWN', 'An error occurred');
    }
  }
}

class ApiClient {
  private baseUrl: string = API_BASE_URL;

  private getStoredAccessToken(): string | null {
    try {
      const authState = sessionStorage.getItem('auth_state');
      if (authState) {
        const parsed = JSON.parse(authState);
        if (typeof parsed?.token === 'string' && parsed.token.trim() !== '') {
          return parsed.token;
        }
      }
    } catch {
      // ignore invalid storage payload
    }

    const legacy = localStorage.getItem('authToken');
    return legacy && legacy.trim() !== '' ? legacy : null;
  }

  setBaseUrl(url: string): void {
    this.baseUrl = url;
  }

  async request<T>(
    method: string,
    endpoint: string,
    options: ApiRequestOptions = {}
  ): Promise<T> {
    const { headers = {}, params = {}, tenantId, body } = options;

    let url = `${this.baseUrl}${endpoint}`;
    
    const queryParams = new URLSearchParams();
    Object.entries(params).forEach(([key, value]) => {
      if (value !== undefined && value !== null) {
        queryParams.append(key, String(value));
      }
    });

    if (queryParams.toString()) {
      url += `?${queryParams.toString()}`;
    }

    const requestHeaders: Record<string, string> = {
      'Content-Type': 'application/json',
    };

    const isPublicAuthEndpoint =
      endpoint.startsWith('/api/auth/magic-link') ||
      endpoint === '/api/auth/sso/callback' ||
      endpoint === '/api/auth/tenant/callback' ||
      endpoint === '/api/auth/sso/complete-tenant';

    // Never attach a stale bearer token to public auth endpoints.
    // EasyAuth can reject malformed/expired Authorization headers before API logic runs.
    if (!isPublicAuthEndpoint) {
      const token = authService.getAccessToken() || this.getStoredAccessToken();
      if (token) {
        requestHeaders['Authorization'] = `Bearer ${token}`;
      }
    }

    // Spread custom headers AFTER automatic token so explicit headers win
    Object.assign(requestHeaders, headers);

    if (tenantId) {
      requestHeaders['X-Tenant-Id'] = tenantId;
    }

    try {
      const response = await fetch(url, {
        method,
        headers: requestHeaders,
        credentials: 'include',
        ...(body !== undefined ? { body } : {}),
      });

      if (!response.ok) {
        const errorData = await response.json().catch(() => ({}));
        throw ApiError.fromResponse(response, errorData);
      }

      if (response.status === 204) {
        return undefined as T;
      }

      const raw = await response.json();
      return unwrapResponse<T>(raw);
    } catch (error) {
      if (error instanceof ApiError) {
        throw error;
      }
      
      if (error instanceof TypeError && error.message.includes('fetch')) {
        throw new ApiError(0, 'NETWORK_ERROR', 'Network connection failed');
      }
      
      throw error;
    }
  }

  async get<T>(
    endpoint: string,
    params?: Record<string, string | number | boolean>,
    token?: string
  ): Promise<T> {
    return this.request<T>('GET', endpoint, { params, headers: token ? { Authorization: `Bearer ${token}` } : {} });
  }

  async post<T>(
    endpoint: string,
    data?: unknown,
    options?: ApiRequestOptions
  ): Promise<T> {
    return this.request<T>('POST', endpoint, {
      ...options,
      headers: {
        ...options?.headers,
        'Content-Type': 'application/json',
      },
      body: JSON.stringify(data),
    } as ApiRequestOptions);
  }

  async put<T>(
    endpoint: string,
    data?: unknown,
    options?: ApiRequestOptions
  ): Promise<T> {
    return this.request<T>('PUT', endpoint, {
      ...options,
      headers: {
        ...options?.headers,
        'Content-Type': 'application/json',
      },
      body: JSON.stringify(data),
    } as ApiRequestOptions);
  }

  async patch<T>(
    endpoint: string,
    data?: unknown,
    options?: ApiRequestOptions
  ): Promise<T> {
    return this.request<T>('PATCH', endpoint, {
      ...options,
      headers: {
        ...options?.headers,
        'Content-Type': 'application/json',
      },
      body: JSON.stringify(data),
    } as ApiRequestOptions);
  }

  async delete<T>(
    endpoint: string,
    options?: ApiRequestOptions
  ): Promise<T> {
    return this.request<T>('DELETE', endpoint, options);
  }

  async upload<T>(
    endpoint: string,
    file: File,
    additionalData?: Record<string, string>,
    _options?: ApiRequestOptions
  ): Promise<T> {
    const formData = new FormData();
    formData.append('file', file);
    
    if (additionalData) {
      Object.entries(additionalData).forEach(([key, value]) => {
        formData.append(key, value);
      });
    }

    const headers: Record<string, string> = {};
    const token = authService.getAccessToken();
    if (token) {
      headers['Authorization'] = `Bearer ${token}`;
    }

    const response = await fetch(`${this.baseUrl}${endpoint}`, {
      method: 'POST',
      headers,
      body: formData,
      credentials: 'include',
    });

    if (!response.ok) {
      throw ApiError.fromResponse(response);
    }

    const raw = await response.json();
    return unwrapResponse<T>(raw);
  }
}

export const apiClient = new ApiClient();

export const api = {
  async getAlerts(filters?: {
    status?: string;
    severity?: string;
    service?: string;
    assignedToMe?: boolean;
    limit?: number;
    continuationToken?: string;
  }): Promise<{ alerts: any[]; continuationToken?: string }> {
    return apiClient.get('/api/alerts', filters as Record<string, string | number | boolean>);
  },

  async getAlert(alertId: string): Promise<any> {
    return apiClient.get(`/api/alerts/${alertId}`);
  },

  async acknowledgeAlert(alertId: string): Promise<void> {
    return apiClient.post(`/api/alerts/${alertId}/ack`);
  },

  async closeAlert(alertId: string, resolutionNote?: string): Promise<void> {
    return apiClient.post(`/api/alerts/${alertId}/close`, { resolutionNote });
  },

  async addNote(alertId: string, content: string): Promise<void> {
    return apiClient.post(`/api/alerts/${alertId}/notes`, { content });
  },

  async assignAlert(alertId: string, userId: string): Promise<void> {
    return apiClient.post(`/api/alerts/${alertId}/assign`, { assignToUserId: userId });
  },

  async getUsers(): Promise<any[]> {
    return apiClient.get('/api/users');
  },

  async createUser(data: { email: string; displayName: string; phone?: string; roles?: string[] }): Promise<any> {
    return apiClient.post('/api/users', data);
  },

  async updateUser(userId: string, data: Partial<any>): Promise<any> {
    return apiClient.patch(`/api/users/${userId}`, data);
  },

  async getGroups(): Promise<any[]> {
    return apiClient.get('/api/groups');
  },

  async getRoles(): Promise<any[]> {
    return apiClient.get('/api/roles');
  },

  async getIntegrations(): Promise<any[]> {
    return apiClient.get('/api/integrations');
  },

  async createIntegration(data: { type: string; name: string; config: any }): Promise<any> {
    return apiClient.post('/api/integrations', data);
  },

  async getRoutingRules(): Promise<any[]> {
    return apiClient.get('/api/routing-rules');
  },

  async createRoutingRule(data: any): Promise<any> {
    return apiClient.post('/api/routing-rules', data);
  },

  async updateRoutingRule(ruleId: string, data: any): Promise<any> {
    return apiClient.patch(`/api/routing-rules/${ruleId}`, data);
  },

  async getSchedules(): Promise<any[]> {
    return apiClient.get('/api/schedules');
  },

  async getOnCallSchedule(): Promise<any> {
    return apiClient.get('/api/schedules/current-oncall');
  },

  async getAuditLog(filters?: {
    tenantId?: string;
    action?: string;
    fromDate?: string;
    toDate?: string;
    limit?: number;
  }): Promise<any[]> {
    return apiClient.get('/api/platform/audit', filters as Record<string, string | number | boolean>);
  },

  async getTenants(): Promise<any[]> {
    return apiClient.get('/api/platform/tenants');
  },

  async createTenant(data: {
    name: string;
    region: string;
    plan: string;
    retentionDays: number;
    adminEmail: string;
    adminName: string;
  }): Promise<any> {
    return apiClient.post('/api/platform/tenants', data);
  },

  async getTenant(tenantId: string): Promise<any> {
    return apiClient.get(`/api/platform/tenants/${tenantId}`);
  },

  async updateTenant(tenantId: string, data: Partial<any>): Promise<any> {
    return apiClient.patch(`/api/platform/tenants/${tenantId}`, data);
  },

  async suspendTenant(tenantId: string): Promise<void> {
    return apiClient.post(`/api/platform/tenants/${tenantId}/suspend`);
  },

  async getTenantUsage(tenantId: string, days?: number): Promise<any> {
    const params: Record<string, string | number | boolean> = {};
    if (days !== undefined) params['days'] = days;
    return apiClient.get(`/api/platform/tenants/${tenantId}/usage`, params);
  },

  async regenerateApiKey(tenantId: string, keyId: string): Promise<{ key: string }> {
    return apiClient.post(`/api/platform/tenants/${tenantId}/apikeys/${keyId}/regenerate`);
  },

  async getPlatformStats(): Promise<any> {
    return apiClient.get('/api/platform/stats');
  },

  async getPlatformUsage(dateRange?: string): Promise<any> {
    const params: Record<string, string | number | boolean> = {};
    if (dateRange !== undefined) params['dateRange'] = dateRange;
    return apiClient.get('/api/platform/usage', params);
  },
};
